import { useRef, useEffect, useCallback } from 'react';

/**
 * Shared hook for workflow step management across pages
 * 
 * Provides consistent workflow progression logic with:
 * - Step completion tracking
 * - Step navigation
 * - Mount detection to prevent auto-progression on page refresh
 * 
 * @param dispatch - UI reducer dispatch function
 * @returns Object with step management functions and refs
 * 
 * @example
 * const { markStepCompleted, moveToStep, isMountedRef } = useWorkflowSteps(dispatch);
 * 
 * // Mark step as completed and auto-advance
 * useEffect(() => {
 *   if (!isMountedRef.current) return;
 *   if (condition) {
 *     markStepCompleted(1);
 *     moveToStep(2);
 *   }
 * }, [condition, markStepCompleted, moveToStep]);
 */
/** Actions this hook dispatches; a subset of each page reducer's action union. */
type WorkflowStepAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'MARK_STEP_COMPLETED'; payload: number };

export const useWorkflowSteps = (dispatch: React.Dispatch<WorkflowStepAction>) => {
  const isMountedRef = useRef(false);

  // Initialize workflow to step 1 on mount
  useEffect(() => {
    dispatch({ type: 'SET_STEP', payload: 1 });
    
    // Delay mounting flag to prevent auto-progression on page refresh
    setTimeout(() => {
      isMountedRef.current = true;
    }, 100);
  }, [dispatch]);

  /**
   * Mark a workflow step as completed
   * @param stepId - The step number to mark as completed
   */
  const markStepCompleted = useCallback((stepId: number) => {
    dispatch({ type: 'MARK_STEP_COMPLETED', payload: stepId });
  }, [dispatch]);

  /**
   * Navigate to a specific workflow step
   * @param stepId - The step number to navigate to
   */
  const moveToStep = useCallback((stepId: number) => {
    dispatch({ type: 'SET_STEP', payload: stepId });
  }, [dispatch]);

  /**
   * Auto-progress to next step if condition is met
   * @param condition - Boolean condition to check
   * @param currentStep - Current active step
   * @param fromStep - Step to mark as completed
   * @param toStep - Step to navigate to
   */
  const autoProgressStep = useCallback((
    condition: boolean,
    currentStep: number,
    fromStep: number,
    toStep: number
  ) => {
    if (!isMountedRef.current || !condition) return;
    
    markStepCompleted(fromStep);
    if (currentStep === fromStep) {
      moveToStep(toStep);
    }
  }, [markStepCompleted, moveToStep]);

  return {
    isMountedRef,
    markStepCompleted,
    moveToStep,
    autoProgressStep
  };
};
